import React, { Component } from "react";
import { NavLink } from "react-router-dom";
import { connect } from "react-redux";
import clsx from "clsx";
import debounce from "lodash.debounce";
import SearchIcon from "@material-ui/icons/Search";
import { IPeerSupportActionDispatches, mapPeerSupportDispatchToProps } from "../../redux/actions/peerSupport";
import { IPeerSupportState, mapPeerSupportStateToProps } from "../../redux/reducers/peerSupport";
import GoogleMap from "../google-maps/MapView";
import {
	MapMarkerFacility,
	MapMarkerAgent,
	MapMarkerClusteredAgents,
	MapMarkerClusteredFacilities,
} from "../google-maps/MapMarker";
import GoogleAutoComplete from "../google-maps/AutoComplete";
import { Place, getGeoLocation, UnmatchedPlace } from "../../models/googleMaps";
import FacilitiesList from "./facilities/FacilitiesList";
import ClientFirebase from "../../laco-common/firebase/web";
import AgentsList from "./agents/AgentsList";
import UILoader from "../ui/UILoader";
import UISearchInput from "../ui/UISearchInput";
import { IPSFacility, IPSAgent, PSRole, getPSRole } from "../../models/peer-support";
import { WidgetProps } from "../widgets";
import { AgentMarkerData } from "../../models/peer-support/markers";
import { withAuth, IAuthProps } from "../../laco-common/auth/AuthContext";
import FormWidget from "../siteForms/FormWidget";
import { IForm } from "../../laco-common";
import { contactAgent } from "../../laco-common/base/firestore-utils";
import NotificationsService from "../../laco-common/services/notifications";
import { IUserReport } from "../../laco-common/base/models/userReport";
import { IS_STANDALONE_PS_SITE, IS_EMBED } from ".";
import { DCASearch } from "./DCASearch";
import { Address, GeoLocation } from "../../laco-common/base/models/address";
import { firewireApp } from "../../firewire";
import PS_LOGO from "../../laco-common/assets/images/PS-logo.png";
import "./Home.scss";

type PeerSupportHomeProps = {} & WidgetProps;

type PeerSupportHomeState = {
	searchQuery: string;
	geoLocation: GeoLocation;
	mapBounds: google.maps.LatLngBounds;
	center: google.maps.LatLng;
	facilities: IPSFacility[];
	agents: IPSAgent[];
	clusteredFacilities: IPSFacility[];
	clusteredAgents: AgentMarkerData[];
	loading: boolean;
};

class PeerSupportHome extends Component<
	IPeerSupportActionDispatches & IPeerSupportState & PeerSupportHomeProps & IAuthProps,
	PeerSupportHomeState
> {
	// static contextType: Context<IAuthContext> = AuthContext;
	private readonly handleSearch = debounce(this.onSearch.bind(this), 700, { trailing: true });

	state = {
		searchQuery: "",
		geoLocation: undefined,
		mapBounds: undefined,
		center: undefined,
		loading: true,
		facilities: this.props.facilities,
		agents: this.props.agents,
		clusteredFacilities: [],
		clusteredAgents: [],
	};

	componentDidMount() {
		const promises = [this.props.loadFacilities()];
		if (!IS_STANDALONE_PS_SITE) {
			promises.push(this.props.loadAgents());
		}
		Promise.all(promises).finally(() => {
			this.loadFacilitiesAndAgentsInView();
			this.setState({ loading: false });
		});
	}

	loadFacilitiesAndAgentsInView(bounds?: google.maps.LatLngBounds) {
		const facilities: IPSFacility[] = this.getFilteredByBounds(this.props.facilities, bounds, "address");
		const agents: IPSAgent[] = IS_STANDALONE_PS_SITE
			? []
			: this.getFilteredByBounds(this.props.agents, bounds, "homeAddress", "workAddress");
		this.setState({ facilities, agents });
		this.setClusteredMarkers(facilities, agents);
	}

	setClusteredMarkers(facilities: IPSFacility[] = this.state.facilities, agents: IPSAgent[] = this.state.agents) {
		const clusteredFacilities: IPSFacility[] = facilities.filter(
			(facility: IPSFacility) => facility.showZipCodeOnly === "Yes"
		);
		const clusteredAgents: AgentMarkerData[] = [];
		agents.forEach((agent: IPSAgent) => {
			if (agent.workAddress && agent.showWorkZipCodeOnly === "Yes") {
				clusteredAgents.push({
					id: agent.id,
					name: `${agent.first_name} ${agent.last_name}`,
					address: agent.workAddress,
					addressType: "Work",
				});
			} else {
				if (agent.homeAddress && agent.showHomeZipCodeOnly === "Yes") {
					clusteredAgents.push({
						id: agent.id,
						name: `${agent.first_name} ${agent.last_name}`,
						address: agent.homeAddress,
						addressType: "Home",
					});
				}
			}
		});
		this.setState({ clusteredFacilities, clusteredAgents });
	}

	getFilteredByBounds(data: any[], bounds?: google.maps.LatLngBounds, ...addressFields): any[] {
		if (!bounds || bounds.isEmpty()) return data;
		const filtered = [];
		data.forEach((item) => {
			if (this.isInMapBounds(item, bounds, addressFields)) {
				filtered.push(item);
			}
		});
		return filtered;
	}

	isInMapBounds(item, bounds: google.maps.LatLngBounds, addressFields: string[]): boolean {
		let inBounds: boolean = false;
		let isEmpty: boolean = true;

		addressFields.forEach((field: string) => {
			const address: Address = item[field];
			if (address && address.lat && address.lng) {
				if (isEmpty) {
					// prev address was not empty
					isEmpty = false;
				}
				if (bounds.contains({ lat: address.lat, lng: address.lng })) {
					inBounds = true;
					return inBounds;
				}
			}
		});
		return inBounds || isEmpty;
	}

	onSearch(val: string) {
		console.log("onSearch:", val);
		this.setState({ searchQuery: val });
	}

	onAddressAutoComplete(place: Place | UnmatchedPlace, zipCode: string) {
		console.log("onAddressAutoComplete:", place, zipCode);
		const geoLocation: GeoLocation = getGeoLocation(place as Place);
		if (geoLocation) {
			this.setState({ geoLocation });
		} else {
			this.setState({ searchQuery: (place as UnmatchedPlace).name });
		}
	}

	onMapBoundsUpdated(bounds: google.maps.LatLngBounds, center: google.maps.LatLng) {
		console.log("onMapBoundsUpdated:", bounds);
		if (
			// no change
			bounds === this.state.mapBounds ||
			(this.state.mapBounds &&
				!this.state.mapBounds.isEmpty() &&
				bounds.getNorthEast().lng === this.state.mapBounds.getNorthEast().lng &&
				bounds.getNorthEast().lat === this.state.mapBounds.getNorthEast().lat &&
				bounds.getSouthWest().lng === this.state.mapBounds.getSouthWest().lng &&
				bounds.getSouthWest().lat === this.state.mapBounds.getSouthWest().lat)
		)
			return;
		this.setState({ mapBounds: bounds, center });
		this.loadFacilitiesAndAgentsInView(bounds);
	}

	contactPSGuest(values: { phone: string } & any, formConfig: IForm) {
		console.log("contactPSGuest:", values, formConfig);
		return this.contactPSAgent({
			user_id: "ps_guest_contact",
			first_name: "",
			last_name: "",
			email: "",
			...values,
		});
	}

	contactPSAgent(data: IUserReport) {
		console.log("contactPSAgent:", data);
		return contactAgent(firewireApp, data)
			.then(() => {
				NotificationsService.success("A Peer Support member will contact you soon.");
			})
			.catch((err) => {
				console.error(err);
				NotificationsService.error("Something went wrong. Please try again later.");
			});
	}

	renderForm() {
		if (IS_STANDALONE_PS_SITE)
			return <FormWidget formId="peer-support-landing" isNested={true} omitContactFields={true} />;
		return (
			<FormWidget
				formId="peer-support-guest-contact"
				isNested={true}
				omitContactFields={true}
				onFormSubmit={(formId: string, values, formConfig: IForm) => this.contactPSGuest(values, formConfig)}
			/>
		);
	}

	render() {
		const userRole: PSRole = getPSRole(this.props.authContext, ClientFirebase);
		return (
			<div
				className={clsx(
					!IS_EMBED && "container",
					IS_EMBED && "p-2",
					"peer-support-home",
					this.props.isNested && "mt-4_5 mb-5",
					!this.props.isNested && "section-padding"
				)}
			>
				<div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2">
					<h2>Available Clinicians{!IS_STANDALONE_PS_SITE && " and Peers"}</h2>
					{this.props.authContext.isAuthenticated && !!this.props.authContext.userPayload && (
						<button
							className="btn btn-secondary py-0 px-3 d-inline-flex align-items-center w-100-sm justify-content-center"
							onClick={() => {
								this.contactPSAgent({
									user_id: this.props.authContext.userPayload.uid,
									email: this.props.authContext.userPayload.email,
									first_name: this.props.authContext.userPayload.profile.first_name,
									last_name: this.props.authContext.userPayload.profile.last_name,
									mobile_number: this.props.authContext.userPayload.profile.mobile_number,
								});
							}}
						>
							Contact PS
							<img src={PS_LOGO} width={35} className="ml-1" style={{ width: "35px", height: "35px" }} />
						</button>
					)}
				</div>

				<UISearchInput
					className="w-100 mb-4 d-none dana TODO"
					inputStyle={{ width: "100%" }}
					placeholder="Search by address or zip code"
					onSearch={this.handleSearch}
				/>
				<GoogleAutoComplete
					className="ui-search-input-container"
					placeholder="Search by address or zip code"
					onPlaceSelected={this.onAddressAutoComplete.bind(this)}
				>
					<SearchIcon
						className={clsx("position-absolute mt-3")}
						style={{ marginLeft: "15px", marginTop: "1.2rem" }}
					/>
				</GoogleAutoComplete>

				<GoogleMap
					address={this.state.searchQuery}
					center={this.state.geoLocation}
					onBoundsUpdated={this.onMapBoundsUpdated.bind(this)}
					// zoom={13}
					// clusteredMarkers={this.state.clusteredMarkers}
				>
					{(this.props.facilities || []).map((f: IPSFacility, i: number) => {
						if (!f.address || !f.address.lat || f.showZipCodeOnly === "Yes") return null;
						return <MapMarkerFacility facility={f} key={f.id} lat={f.address.lat} lng={f.address.lng} />;
					})}
					{(this.props.agents || []).map((a: IPSAgent, i: number) => {
						if (
							!!a.workAddress &&
							!!a.workAddress.lat &&
							a.showWorkZipCodeOnly !== "Yes"
							// &&
							// a.workAddress.lat !== a.homeAddress?.lat &&
							// a.workAddress.lng !== a.homeAddress?.lng
						)
							return (
								<MapMarkerAgent
									agent={a}
									key={a.id}
									lat={a.workAddress.lat}
									lng={a.workAddress.lng}
									addressType="Work"
								/>
							);
					})}
					{(this.props.agents || []).map((a: IPSAgent, i: number) => {
						if (!!a.homeAddress && !!a.homeAddress.lat && a.showHomeZipCodeOnly !== "Yes")
							return (
								<MapMarkerAgent
									agent={a}
									key={a.id}
									lat={a.homeAddress.lat}
									lng={a.homeAddress.lng}
									addressType="Home"
								/>
							);
					})}
					{!!this.state.center && (
						<MapMarkerClusteredAgents
							agents={this.state.clusteredAgents}
							center={this.state.center}
							// lat={this.state.center.lat()}
							// lng={this.state.center.lng()}
						/>
					)}
					{!!this.state.center && (
						<MapMarkerClusteredFacilities
							facilities={this.state.clusteredFacilities}
							center={this.state.center}
							// lat={this.state.center.lat()}
							// lng={this.state.center.lng()}
						/>
					)}
				</GoogleMap>

				<div className="row results-container">
					<div className="col-12 col-md-5">
						<h2 className={clsx("mb-3_5", "title-link")}>
							<NavLink to="/ps/facilities">Facilities and Clinicians</NavLink>
							<DCASearch />
						</h2>
						{this.state.loading && <UILoader display={true} />}
						{!this.state.loading && (
							<FacilitiesList
								facilities={this.state.facilities}
								omitActions={true}
								renderList={true}
								onEditFacility={null}
								onDeleteFacility={null}
								noDataMsg="No facilities in the selected area"
							/>
						)}
					</div>
					<div className="d-none d-md-block col-md-1"></div>
					<div className="col-12 col-md-6">
						{userRole !== "guest" && !IS_STANDALONE_PS_SITE && (
							<h2 className="mb-3_5 title-link">
								<NavLink to="/ps/peers">Peers</NavLink>
							</h2>
						)}
						{this.state.loading && <UILoader display={true} />}
						{!this.state.loading && (
							<>
								{(userRole === "guest" || IS_STANDALONE_PS_SITE) && this.renderForm()}
								{userRole !== "guest" && !IS_STANDALONE_PS_SITE && (
									<AgentsList
										agents={this.state.agents}
										omitActions={true}
										// omitAddress={true}
										renderList={true}
										onEditAgent={null}
										onDeleteAgent={null}
										noDataMsg="No agents in the selected area"
									/>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		);
	}
}

export default connect<IPeerSupportState, IPeerSupportActionDispatches, PeerSupportHomeProps>(
	mapPeerSupportStateToProps,
	mapPeerSupportDispatchToProps
)(withAuth(PeerSupportHome));
